import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type CharterSeatConfigurationOverrideParams,
  type CharterSeatConfigurationOverrideUpdatePayload,
  fetchCharterSeatConfigurationOverrideDetail,
  fetchCharterSeatConfigurationOverrideFlights,
  updateCharterSeatConfigurationOverrides,
} from '~/services/CharterService';

import { charterSeatConfigurationOverrideQueryKeys } from './charterSeatConfigurationOverrideQueryKeys';

export function useGetCharterSeatConfigurationOverrideFlights(params: CharterSeatConfigurationOverrideParams) {
  return useQuery({
    queryKey: charterSeatConfigurationOverrideQueryKeys.list(params),
    queryFn: async ({ signal }) => fetchCharterSeatConfigurationOverrideFlights(params, { signal }),
    refetchOnWindowFocus: false,
  });
}

export function useGetCharterSeatConfigurationOverrideDetail(flightInstanceId: string | null) {
  const isEnabled = Boolean(flightInstanceId);

  return useQuery({
    queryKey: flightInstanceId
      ? charterSeatConfigurationOverrideQueryKeys.detail(flightInstanceId)
      : [...charterSeatConfigurationOverrideQueryKeys.details(), 'disabled'],
    queryFn: async ({ signal }) => {
      if (!flightInstanceId) {
        throw new Error('Flight instance ID is required.');
      }
      return fetchCharterSeatConfigurationOverrideDetail(flightInstanceId, { signal });
    },
    enabled: isEnabled,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

export function useUpdateCharterSeatConfigurationOverrides(flightInstanceId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CharterSeatConfigurationOverrideUpdatePayload) => {
      if (!flightInstanceId) {
        throw new Error('Flight instance ID is required.');
      }
      return updateCharterSeatConfigurationOverrides(flightInstanceId, payload);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: charterSeatConfigurationOverrideQueryKeys.lists() }),
        flightInstanceId
          ? queryClient.invalidateQueries({
              queryKey: charterSeatConfigurationOverrideQueryKeys.detail(flightInstanceId),
            })
          : Promise.resolve(),
      ]);
    },
  });
}
